import { Content, Filter } from '@23degrees/shared-code/models';
import { MessageBus } from '@23degrees/shared/bus';
import { StyleService } from '@23degrees/shared/services/style';
import { Component, Input, OnInit, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAP_MESSAGES, MESSAGE_RECIPIENTS } from '@config';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';

import { ListSelectorComponent } from '@23degrees/ui/molecules/list-selector';
import { MenuButtonComponent } from '@23degrees/ui/molecules/menu-button';
import { NgClass, NgStyle } from '@angular/common';
import { ResponsiveStylePipe } from '@23degrees/shared/pipes';

@Component({
    selector: 'annotation-filter',
    templateUrl: './annotation-filter.component.html',
    styleUrls: ['./annotation-filter.component.scss'],
    imports: [NgClass, NgStyle, MenuButtonComponent, ListSelectorComponent, FormsModule, ResponsiveStylePipe]
})
export class AnnotationFilterComponent implements OnInit {
  private responsiveStylePipe = inject(ResponsiveStylePipe)
  private messageBus = inject(MessageBus);

  // TODO: signalize

  @Input() content: Content;
  @Input() forceUpdate: any;

  public closeSelect: boolean = false;
  public activeFilter: string;

  public baseOriginPosition = ['start', 'bottom'];
  public baseOverlayPosition = ['start', 'top'];

  public popupPositions = {};

  get annotations() {
    return this.content.typeSpecific.map.annotations;
  }

  get filters(): Filter[] {
    return get(this.content.typeSpecific.map, 'annotations.custom.filter');
  }

  get categories() {
    return get(this.content.typeSpecific.map, 'annotations.custom.categories');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.content || !changes.forceUpdate) return;
    // build localstorage for changes if it doesnt exist
    // console.log('[AnnotationFilter] - Changes');
    if (this.content?.typeSpecific?.map?.annotations?.custom?.filter && this.content.typeSpecific.map.annotations.custom.filter.length > 0) {
      // build selected values
      for (const filter of this.content.typeSpecific.map.annotations.custom.filter) {
        if (!filter.localStorage) filter.localStorage = {};
        // sorting of values
        // console.log("cat values: ",orderBy(this.categories[filter.categoryId].values,["label"], "asc"));
        if (filter.options.sort === 'asc') {
          const vals = JSON.parse(JSON.stringify(this.categories[filter.categoryId].values));
          filter.localStorage.categoryValues = orderBy(vals, ['label'], 'asc');
        }

        if (filter.options.sort === 'desc') {
          const vals = JSON.parse(JSON.stringify(this.categories[filter.categoryId].values));
          filter.localStorage.categoryValues = orderBy(vals, ['label'], 'desc');
        }

        if (filter.options.sort === 'custom') {
          const vals = JSON.parse(JSON.stringify(this.categories[filter.categoryId].values));
          filter.localStorage.categoryValues = vals;
        }

        filter.localStorage.allValueIds = filter.localStorage.categoryValues.map((el) => el.id);
        // only change selected values if we have no init
        if (!filter.localStorage.selectedValueIds) {
          filter.localStorage.selectedValueIds = [];
          if (filter.localStorage.categoryValues && filter.localStorage.categoryValues.length > 0) {
            if (filter.options.initialState === 'all') {
              filter.localStorage.selectedValueIds = filter.localStorage.categoryValues.map((el) => el.id);
            }

            if (filter.options.initialState === 'custom') {
              // setting from non localstorage
              filter.localStorage.selectedValueIds = filter.selectedValueIds;
            }

            if (filter.options.initialState === 'first') {
              filter.localStorage.selectedValueIds = [filter.localStorage.categoryValues[0].id];
            }

            if (filter.options.initialState === 'last') {
              filter.localStorage.selectedValueIds = [filter.localStorage.categoryValues[filter.localStorage.categoryValues.length - 1].id];
            }
            // setting of selectedValues
          }

          filter.selectedValueIds = filter.localStorage.selectedValueIds;
        }
        // console.log("[Done Changes]",filter);
      }
    }
  }

  ngOnInit(): void {}

  emitChange() {
    console.log('[AnnotationFilter] - emitting change', this.content.typeSpecific.map.annotations.custom.filter);
    this.messageBus.emit(MAP_MESSAGES.DetectChanges, [MESSAGE_RECIPIENTS.Map], {}, this.content.localStorage.referenceID);
    if (this.annotations?.custom.cluster.enabled) {
      this.messageBus.emit(MAP_MESSAGES.ClusterUpdate, [MESSAGE_RECIPIENTS.Map], {}, this.content.localStorage.referenceID);
    }

    this.messageBus.emit(MAP_MESSAGES.ZoomToMarkers, [MESSAGE_RECIPIENTS.Map], {}, this.content.localStorage.referenceID);
    // this.messageBus.emit(
    //     REQUEST_MESSAGES.ContentUpdate,
    //     [MESSAGE_RECIPIENTS.Map],
    //     { slug: this.content.slug, content: this.content },
    //     this.content.localStorage.referenceID
    // );
  }

  style(filter: Filter) {
    const style = JSON.parse(JSON.stringify(filter.style[filter.options.appearance]));
    if (filter.categoryId === this.activeFilter && filter.options.appearance === 'dropdown' && filter.style.dropdown.popup.container.applyStyleToSourceButton) {
      style.font.color = filter.style.dropdown.popup.items.font.color;
      style.background = filter.style.dropdown.popup.container.background;
    }

    const margin = this.responsiveStylePipe.transform(style.font.margin, 'spacing-horizontal');

    return {
      ...this.responsiveStylePipe.transform(style.font),
      maxWidth: `calc(100% - ${margin.left} - ${margin.right})`,
      borderRadius: `${style.borderRadius}px`,
      backgroundColor: style.background.backgroundColor,
      ...this.responsiveStylePipe.transform(style.border, 'border'),
    };
  }

  filterStyle(filter) {
    return filter.style[filter.options.appearance];
  }

  opened(isOpen, filter) {
    // console.log('OPEN', isOpen, filter);
    if (isOpen) {
      this.activeFilter = filter;
    } else if (this.activeFilter === filter) {
      this.activeFilter = null;
    }
  }

  singleOutElement(filter: Filter, item) {
    filter.selectedValueIds = [item.id];
    filter.localStorage.selectedValueIds = [item.id];
    this.emitChange();
    // if(filter.options.initialState === 'custom'){
    this.messageBus.emit(MAP_MESSAGES.DetectChanges, [MESSAGE_RECIPIENTS.MapEditor], {}, this.content.localStorage.referenceID);
    // }
  }

  setValues(filter: Filter, event) {
    filter.selectedValueIds = event;
    filter.localStorage.selectedValueIds = event;
    this.emitChange();

    // if(filter.options.initialState === 'custom'){
    this.messageBus.emit(MAP_MESSAGES.DetectChanges, [MESSAGE_RECIPIENTS.MapEditor], {}, this.content.localStorage.referenceID);
    // }
  }

  trackByFunction = (item) => {
    return item.value;
  };

  public setPopupPositions(filter, positions) {
    this.popupPositions[filter] = positions;
  }

  public getPopupPosition(filter) {
    return this.popupPositions[filter] || { overlayPosition: this.baseOverlayPosition, originPosition: this.baseOriginPosition };
  }
}
